import { alert, defaults, Stack, Options as PnotifyOptions } from '@pnotify/core';
import '@pnotify/core/dist/PNotify.css';
import '@pnotify/core/dist/Material.css';
import type { AriaService } from './mx-aria-service';
import { I18n } from '../../i18n/mx-internationalization';
import { material } from 'angular';

const myStack = new Stack({
	dir1: 'up',
	dir2: 'right',
	push: 'top',
	maxStrategy: 'close',
	modal: false,
	maxOpen: 10,
	firstpos1: 30,
	firstpos2: 30,
});

Object.assign(defaults, {
	stack: myStack,
	styling: 'alert',
	icons: 'alert-icon material',
});

export class NotificationService {
	static readonly $inject = ['$mdDialog', 'mx.internationalization', 'mx.shell.AriaService'];

	constructor(
		private readonly $mdDialog: material.IDialogService,
		private readonly i18n: I18n,
		private readonly ariaService: AriaService
	) {}

	notify(notification: PnotifyOptions & { ariaMessage?: string }) {
		const notifyObj = alert(notification);
		notifyObj.refs.iconContainer?.setAttribute('aria-hidden', 'true');

		this.ariaService.readMessage(notification.ariaMessage || (notification.title ? notification.title + '. ' : '') + (notification.text || ''));
	}

	info(message: string, title?: string, asHtml = false): void {
		this.notify({
			text: message,
			title: title,
			type: 'info',
			titleTrusted: asHtml,
			textTrusted: asHtml,
		});
	}

	warning(message: string, title?: string, asHtml = false): void {
		this.notify({
			text: message,
			title: title,
			type: 'notice',
			titleTrusted: asHtml,
			textTrusted: asHtml,
		});
	}

	error(message: string, title?: string, asHtml = false): void {
		this.notify({
			text: message,
			title: title,
			type: 'error',
			titleTrusted: asHtml,
			textTrusted: asHtml,
		});
	}

	success(message: string, ariaMessage?: string, asHtml = false): void {
		this.notify({
			text: message,
			ariaMessage: ariaMessage,
			type: 'success',
			titleTrusted: asHtml,
			textTrusted: asHtml,
		});
	}

	private wrapHtml(html: string): string {
		return '<div class="mx-dialog-content-message">' + html + '</div>';
	}

	confirm(title = '', prompt = '', okLabel = this.i18n.get('shell.common.ok'), cancelLabel = this.i18n.get('shell.common.cancel')): Promise<unknown> {
		return this.$mdDialog.show(
			this.$mdDialog.confirm().title(title).ariaLabel(this.i18n.get('shell.common.confirmation')).ok(okLabel).cancel(cancelLabel).htmlContent(this.wrapHtml(prompt))
		) as Promise<unknown>;
	}

	confirmHtml(
		title: string,
		html: string,
		okLabel = this.i18n.get('shell.common.ok'),
		cancelLabel = this.i18n.get('shell.common.cancel'),
		extraLines?: Array<string>
	): Promise<unknown> {
		if (Array.isArray(extraLines)) {
			html += extraLines
				.map(function (line) {
					return '<span class="dialog-extra">' + line + '</span>';
				})
				.join('');
		}

		return this.$mdDialog.show(
			this.$mdDialog.confirm().title(title).ariaLabel(this.i18n.get('shell.common.confirmation')).ok(okLabel).cancel(cancelLabel).htmlContent(this.wrapHtml(html))
		) as Promise<unknown>;
	}

	prompt(
		title: string,
		prompt = '',
		placeholder = '',
		initialValue = '',
		required = false,
		okLabel = this.i18n.get('shell.common.ok'),
		cancelLabel = this.i18n.get('shell.common.cancel')
	): Promise<unknown> {
		return this.$mdDialog.show(
			this.$mdDialog
				.prompt()
				.title(title)
				.textContent(prompt)
				.placeholder(placeholder)
				.ariaLabel(placeholder)
				.initialValue(initialValue)
				.required(required)
				.ok(okLabel)
				.cancel(cancelLabel)
		) as Promise<unknown>;
	}

	alert(title: string, html: string, okLabel = this.i18n.get('shell.common.ok'), clickOutsideToClose = true): Promise<unknown> {
		return this.$mdDialog.show(
			this.$mdDialog.alert().title(title).textContent('').ok(okLabel).clickOutsideToClose(clickOutsideToClose).htmlContent(this.wrapHtml(html))
		) as Promise<unknown>;
	}

	customDialog(dialogConfig: material.IDialogOptions): Promise<unknown> {
		return this.$mdDialog.show(dialogConfig) as Promise<unknown>;
	}
}
